import { useCallback, useEffect, useRef, useState } from 'react';

/** Floor for the computed page size so tiny viewports still page usefully. */
export const MIN_DYNAMIC_PAGE_SIZE = 5;

/**
 * Estimated row height used until a real body row has rendered. Matches the
 * standard settings table row: SettingsTd's py={2.5} around one line of text.
 */
const FALLBACK_ROW_HEIGHT_PX = 41;

/**
 * Custom property the hook writes on the scroll box; SettingsTable applies it
 * as the body row height so a full page of rows exactly fills the box and the
 * last row lands on the pagination footer.
 */
export const SETTINGS_ROW_HEIGHT_VAR = '--settings-table-row-height';

/**
 * Computes how many rows fit in a SettingsTable's scroll box, so paginated
 * settings tables show exactly as many rows as the viewport allows instead of
 * a fixed page size, and stretches those rows to close the leftover gap above
 * the footer.
 *
 * Pass `scrollRef` to SettingsTable's `scrollRef` prop. The hook measures the
 * scroll box height minus the sticky header, divides by the natural height of
 * a body row (rows are a consistent height in these tables), and floors the
 * result, clamped to a minimum. Dividing that same space back by the page size
 * gives the row height that leaves no remainder, which it writes to the scroll
 * box as a CSS variable rather than through state, keeping it out of the
 * render path. A ResizeObserver on the scroll box recomputes on window/layout
 * resizes; one on the tbody recomputes when rows first render.
 *
 * No feedback loop: the scroll box is the flex-filled area of
 * SettingsPage variant="table", so its height depends on the viewport, not on
 * how many rows are rendered or how tall they are. The natural row height is
 * measured with the stretch removed and then cached, so the stretch can never
 * feed back into the page size.
 */
export const useDynamicPageSize = (
    minPageSize: number = MIN_DYNAMIC_PAGE_SIZE,
): { scrollRef: (node: HTMLDivElement | null) => void; pageSize: number } => {
    const [pageSize, setPageSize] = useState(minPageSize);
    const observerRef = useRef<ResizeObserver | null>(null);
    /** Row height with no stretch applied, measured once rows first render. */
    const naturalRowHeightRef = useRef<number | null>(null);

    const scrollRef = useCallback(
        (node: HTMLDivElement | null) => {
            observerRef.current?.disconnect();
            observerRef.current = null;
            if (!node) return;

            naturalRowHeightRef.current = null;

            const measureNaturalRowHeight = () => {
                if (naturalRowHeightRef.current != null) return naturalRowHeightRef.current;
                // Drop any stretch first, so this measures the row's own content
                // height rather than the height it was last given.
                node.style.removeProperty(SETTINGS_ROW_HEIGHT_VAR);
                const firstRow = node.querySelector('tbody tr');
                const measured = firstRow ? firstRow.getBoundingClientRect().height : 0;
                if (measured > 0) naturalRowHeightRef.current = measured;
                return measured > 0 ? measured : FALLBACK_ROW_HEIGHT_PX;
            };

            const compute = () => {
                const thead = node.querySelector('thead');
                const headerHeight = thead ? thead.getBoundingClientRect().height : 0;
                const rowHeight = measureNaturalRowHeight();
                const available = node.clientHeight - headerHeight;
                const next = Math.max(minPageSize, Math.floor(available / rowHeight));

                // Spread the remainder over the page's rows so the last row ends
                // on the footer instead of leaving a band of background above it.
                const stretched = Math.max(rowHeight, available / next);
                node.style.setProperty(SETTINGS_ROW_HEIGHT_VAR, `${stretched.toFixed(2)}px`);

                setPageSize((prev) => (prev === next ? prev : next));
            };

            const observer = new ResizeObserver(compute);
            observer.observe(node);
            const tbody = node.querySelector('tbody');
            if (tbody) observer.observe(tbody);
            observerRef.current = observer;
            compute();
        },
        [minPageSize],
    );

    useEffect(() => () => observerRef.current?.disconnect(), []);

    return { scrollRef, pageSize };
};
